import type { DishAttrType, DishAttributesType } from '../../../types/product';
import { MouseEvent, useEffect, useState } from 'react';
import { addOrUpdateCart, deleteProductCart } from '../../../api/cart';
import { useDispatch, useSelector } from 'react-redux';

import { Anchor } from '../../../shared/Anchor';
import { QuantityButton } from '../../../shared/QuantityButton';
import { setCart } from '../../../store/cart/cartSlice';
import type { state } from '../../../types/store/state';
import { toast } from 'react-toastify';
import { useTranslation } from 'next-i18next';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';

interface Props {
  orderBranchProductId?: number;
  storeBranchProductId: number;
  customQuantity?: number;
  added?: boolean;
  dishAttributes?: DishAttrType;
  observation?: string;
  isDish?: boolean;
  productUrl?: number;
  setDishAttributes?: (attr: DishAttrType) => void;
  setObservation?: (attr: string) => void;
  attributes: DishAttributesType;
  disabled?: boolean;
  productName: string;
}

export const AddDishToCart = ({
  customQuantity,
  added,
  storeBranchProductId,
  observation,
  dishAttributes,
  setDishAttributes,
  setObservation,
  orderBranchProductId,
  attributes,
  disabled,
  productName,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cartReference = useSelector((state: state) => state.cart.reference);
  const cartItems = useSelector((state: state) => state.cart.branchStores[0]?.products);

  const [count, setCount] = useState(1);
  const [, setAdded] = useState(false);

  useEffect(() => {
    if (customQuantity) {
      setCount(1);
      setDishAttributes?.({});
      setObservation?.('');
    }
    if (added) setAdded(added);
  }, [customQuantity, setDishAttributes, setObservation, added]);

  const validateMandatory = () => {
    if (!dishAttributes) {
      return true;
    }

    const mandatoryItems = attributes ? Object.keys(attributes).filter((attr) => attributes[attr].mandatory === 'true') : [];
    const selectedItems = Object.keys(dishAttributes).sort();

    if (!mandatoryItems.length) {
      return true;
    }

    if (mandatoryItems.length > 0 && (mandatoryItems.length <= selectedItems.length)) {
      let mandatorySelected = 0;
      for (let i = 0; i < mandatoryItems.length; i++) {
        const elem = selectedItems.filter((attr) => attr === mandatoryItems[i]);
        if (elem?.length && dishAttributes[elem[0]].length) {
          mandatorySelected += 1;
        }
      }
      if (mandatorySelected === mandatoryItems.length) {
        return true;
      }
    }
    return false;
  };

  const isProductInCart = (items: any[]) => {
    return items?.some((item) => 
      item.storeBranchProduct === storeBranchProductId && 
      JSON.stringify(item.attributes) === JSON.stringify(dishAttributes) &&
      item.observation === observation
    );
  };

  const handleCart = async (event: MouseEvent) => {
    event.preventDefault();
    if (!validateMandatory()) {
      toast.warning(t('DishAttributes.mustSelectAttr.toastify.error.title'));
      return;
    }

    if (isProductInCart(cartItems)) {
      toast.warning(t('cart.product.exists.toastify.warning', { productName }));
      return;
    }

    setAdded(true);

    if (count <= 0) {
      const result = await handleToastAsyncAction(
        () => deleteProductCart({
          id: cartReference,
          params: { orderBranchProduct: orderBranchProductId },
        }),
        {
          onSuccessMessage: t('cart.product.delete.toastify.success', { productName }),
          onErrorMessage: t('cart.product.delete.toastify.error', { productName }),
          dispatchAction: () => {
            setAdded(false);
            setCount(1);
          },
          errorLogger: (error) => {
            console.error('Error deleting product from cart:', error);
            Sentry.captureException(error, {
              tags: {
                action: 'delete_product_cart',
                cartReference,
                orderBranchProductId
              },
            });
          },
        }
      );

      if (result.success && result.data) {
        dispatch(setCart(result.data));
      }
      return;
    }

    const result = await handleToastAsyncAction(
      () => addOrUpdateCart({
        id: cartReference,
        params: {
          storeBranchProduct: storeBranchProductId,
          quantity: count,
          observation: observation,
          attributes: dishAttributes || {},
        },
      }),
      {
        onSuccessMessage: t('cart.product.add.toastify.success', { productName }),
        onErrorMessage: t('cart.product.add.toastify.error', { productName }),
        dispatchAction: () => {
          setDishAttributes?.({});
          setObservation?.('');
          setCount(1);
        },
        errorLogger: (error) => {
          console.error('Error adding product to cart:', error);
          Sentry.captureException(error, {
            tags: {
              action: 'add_cart',
              cartReference,
              storeBranchProductId,
              quantity: count
            },
            extra: {
              observation,
              attributes: dishAttributes
            }
          });
        },
      }
    );

    if (result.success && result.data) {
      dispatch(setCart(result.data));
    }
  };

  return (
    <>
      <QuantityButton
        handleAddToCart={() => { }}
        setCount={setCount}
        count={count}
        setAdded={setAdded}
        disabled={disabled}
      />
      {count > 0 && (
        <Anchor
          title={t('shared.product.action.addToCart')}
          size="medium"
          onClick={handleCart}
          disabled={disabled}
        />
      )}
    </>
  );
};
